import type {
  AlertSubscriptionsResponse,
  AnalysisHistoryResponse,
  Article,
  BriefingHistoryResponse,
  CategoriesResponse,
  CategoryBriefingEnvelope,
  ClusterDetailResponse,
  CursorPageRequest,
  MaterialAnalysisEnvelope,
  MaterialClustersPageResponse,
  MaterialLatest,
  MaterialsResponse,
  NotificationChannelsResponse,
  RefreshResponse,
  TelegramLinkResponse,
  TradingFeedOptions,
  TradingFeedResponse,
} from "./types";
import type {
  CalendarEventDetail,
  CalendarListParams,
  CalendarListResponse,
} from "../calendar/types";
import type {
  NewsSignalParams,
  NewsSignalResponse,
  PriceBarsParams,
  PriceBarsResponse,
} from "../trading/material-chart/types";
import { getAccessToken, getActiveOrgId, refreshSession } from "./session";

class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
  }
}

// In dev, requests use relative paths and Vite's proxy forwards /api/v1/* to
// the local backend. In prod, VITE_API_BASE_URL is set to the deployed API
// origin (e.g. https://api.tapelens.com) and the relative path is prefixed.
function apiUrl(path: string): string {
  const base = import.meta.env.VITE_API_BASE_URL as string | undefined;
  if (!base) return path;
  return `${base.replace(/\/$/, "")}${path}`;
}

// Cache key includes the active org id so a cached response from one org
// doesn't 304-feed into another org after a switch. The path alone is not
// enough because the X-Organization-Id header changes the server response.
const etagCache = new Map<string, { etag: string; data: unknown }>();

function etagCacheKey(path: string, orgId: string | null): string {
  return `${orgId ?? "_"}:${path}`;
}

export async function apiFetch<T>(path: string, init?: RequestInit, isRetry = false): Promise<T> {
  const token = await getAccessToken();
  const orgId = getActiveOrgId();
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(init?.headers as Record<string, string> | undefined ?? {}),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (orgId) headers["X-Organization-Id"] = orgId;
  // Auto-default Content-Type when there's a body and the caller didn't set
  // one. Without this, fetch falls back to text/plain and FastAPI receives
  // the body as a string instead of parsing it as JSON (422 from Pydantic).
  if (init?.body != null && !("Content-Type" in headers) && !("content-type" in headers)) {
    headers["Content-Type"] = "application/json";
  }

  const res = await fetch(apiUrl(path), { ...init, headers, credentials: "same-origin" });

  if (res.status === 401 && !isRetry && token) {
    const refreshed = await refreshSession();
    if (refreshed) return apiFetch<T>(path, init, true);
    // Refresh failed → bubble 401 up; UI layer redirects to signin.
  }

  if (res.status === 204) return undefined as T;

  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = (await res.json()) as { message?: string; error?: string };
      detail = body.message ?? body.error ?? detail;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, detail);
  }
  return (await res.json()) as T;
}

function makeTradingFeedPath(id: string, options: TradingFeedOptions = {}): string {
  const params = new URLSearchParams();
  if (options.historyLimit != null) params.set("history_limit", String(options.historyLimit));
  if (options.clusterLookback) params.set("cluster_lookback", options.clusterLookback);
  if (options.clusterLimit != null) params.set("cluster_limit", String(options.clusterLimit));
  const qs = params.toString();
  const path = `/api/v1/materials/${encodeURIComponent(id)}/trading-feed`;
  return qs ? `${path}?${qs}` : path;
}

async function fetchJsonWithEtag<T>(path: string, isRetry = false): Promise<T> {
  const token = await getAccessToken();
  const orgId = getActiveOrgId();
  const cacheKey = etagCacheKey(path, orgId);
  const cached = etagCache.get(cacheKey);
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(cached ? { "If-None-Match": cached.etag } : {}),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (orgId) headers["X-Organization-Id"] = orgId;

  const res = await fetch(apiUrl(path), { headers, credentials: "same-origin" });

  if (res.status === 401 && !isRetry && token) {
    const refreshed = await refreshSession();
    if (refreshed) return fetchJsonWithEtag<T>(path, true);
  }

  if (res.status === 304 && cached) return cached.data as T;
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = (await res.json()) as { message?: string; error?: string; detail?: string };
      detail = body.message ?? body.error ?? body.detail ?? detail;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, detail);
  }

  const data = (await res.json()) as T;
  const etag = res.headers.get("etag");
  if (etag) etagCache.set(cacheKey, { etag, data });
  return data;
}

/**
 * Append `?lang=<code>` to a path (or `&lang=...` if it already has a
 * query string). Two-letter lowercase codes only — anything else is
 * silently ignored so a stray `language_served` value can't propagate
 * back as a bogus query. Used by every content endpoint that supports
 * i18n Slice 2.
 */
function withLang(path: string, lang?: string | null): string {
  if (!lang || !/^[a-z]{2}$/.test(lang)) return path;
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}lang=${lang}`;
}

// Shared cache key for the materials list. Both MaterialPicker and
// AlertSubscriptionsCard call api.listMaterials; using this constant ensures
// they hit the same React Query cache entry instead of issuing two fetches.
export const MATERIALS_QUERY_KEY = ["materials"] as const;

export const api = {
  listMaterials: () => apiFetch<MaterialsResponse>("/api/v1/materials"),
  getMaterialLatest: (id: string) =>
    apiFetch<MaterialLatest>(`/api/v1/materials/${encodeURIComponent(id)}/latest`),
  getAlertSubscriptions: () =>
    apiFetch<AlertSubscriptionsResponse>("/api/v1/me/alert-subscriptions"),
  subscribeAlert: (materialId: string) =>
    apiFetch<void>(
      `/api/v1/me/alert-subscriptions/${encodeURIComponent(materialId)}`,
      { method: "PUT" },
    ),
  unsubscribeAlert: (materialId: string) =>
    apiFetch<void>(
      `/api/v1/me/alert-subscriptions/${encodeURIComponent(materialId)}`,
      { method: "DELETE" },
    ),
  getNotificationChannels: () =>
    apiFetch<NotificationChannelsResponse>("/api/v1/me/notification-channels"),
  setChannelEnabled: (kind: string, enabled: boolean) =>
    apiFetch<void>(`/api/v1/me/notification-channels/${encodeURIComponent(kind)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ enabled }),
    }),
  linkTelegram: () =>
    apiFetch<TelegramLinkResponse>("/api/v1/me/notification-channels/telegram/link", {
      method: "POST",
    }),
  unlinkTelegram: () =>
    apiFetch<void>("/api/v1/me/notification-channels/telegram", { method: "DELETE" }),
  getTradingFeed: (id: string, options?: TradingFeedOptions) =>
    fetchJsonWithEtag<TradingFeedResponse>(makeTradingFeedPath(id, options)),
  /**
   * i18n Slice 2 — current per-material analysis envelope. Returns the
   * `analysis` body alongside `language_served` so callers can render an
   * "Available in English" badge when the requested language wasn't
   * available. Pass `lang` (e.g. user's preferred_language) to request a
   * specific language; omitted/null defers to the API's resolution chain
   * (`?lang=` → `users.preferred_language` → `"en"`).
   */
  getMaterialAnalysis: (id: string, lang?: string | null) =>
    apiFetch<MaterialAnalysisEnvelope>(
      withLang(`/api/v1/materials/${encodeURIComponent(id)}/analysis`, lang),
    ),
  refreshMaterial: (id: string) =>
    apiFetch<RefreshResponse>(
      `/api/v1/materials/${encodeURIComponent(id)}/refresh`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: "manual" }),
      },
    ),
  listCategories: () => apiFetch<CategoriesResponse>("/api/v1/categories"),
  /**
   * Current category briefing envelope (i18n Slice 2). Returns
   * `{ body, language_served, version, ... }` rather than the raw body
   * so callers can drive the "Available in English" badge. The narrative
   * itself lives under `.body` (legacy `CategoryBriefing` shape).
   */
  getCategoryBriefing: (id: string, lang?: string | null) =>
    apiFetch<CategoryBriefingEnvelope>(
      withLang(`/api/v1/categories/${encodeURIComponent(id)}/briefing`, lang),
    ),
  getCluster: (id: string) =>
    apiFetch<ClusterDetailResponse>(`/api/v1/clusters/${encodeURIComponent(id)}`),
  getArticle: (id: string) =>
    apiFetch<Article>(`/api/v1/articles/${encodeURIComponent(id)}`),
  listCalendar: (params?: CalendarListParams) => {
    const qs = new URLSearchParams();
    if (params?.start) qs.set("start", params.start);
    if (params?.end) qs.set("end", params.end);
    if (params?.materials?.length) qs.set("materials", params.materials.join(","));
    if (params?.types?.length) qs.set("types", params.types.join(","));
    if (params?.importance?.length) qs.set("importance", params.importance.join(","));
    if (params?.status?.length) qs.set("status", params.status.join(","));
    if (params?.lang && /^[a-z]{2}$/.test(params.lang)) qs.set("lang", params.lang);
    const q = qs.toString();
    const path = q ? `/api/v1/calendar?${q}` : "/api/v1/calendar";
    return fetchJsonWithEtag<CalendarListResponse>(path);
  },
  getCalendarEvent: (eventId: string, lang?: string | null) =>
    fetchJsonWithEtag<CalendarEventDetail>(
      withLang(
        `/api/v1/calendar/events/${encodeURIComponent(eventId)}`,
        lang,
      ),
    ),

  // News-signal chart endpoints (spec §5.1, §5.2). Both pass through
  // fetchJsonWithEtag so the 30s React Query poll cycles are 304-cheap.
  getMaterialPrices: (params: PriceBarsParams) => {
    const qs = new URLSearchParams();
    qs.set("material_id", params.material_id);
    qs.set("timeframe", params.timeframe);
    qs.set("since", params.since);
    if (params.until) qs.set("until", params.until);
    return fetchJsonWithEtag<PriceBarsResponse>(`/api/v1/prices/bars?${qs.toString()}`);
  },
  getMaterialNewsSignal: (params: NewsSignalParams) => {
    const qs = new URLSearchParams();
    qs.set("timeframe", params.timeframe);
    qs.set("since", params.since);
    if (params.until) qs.set("until", params.until);
    if (params.sources?.length) qs.set("sources", params.sources.join(","));
    const path = `/api/v1/materials/${encodeURIComponent(params.material_id)}/news-signal?${qs.toString()}`;
    return fetchJsonWithEtag<NewsSignalResponse>(path);
  },

  // Cursor-paginated "load older" endpoints. All three share the
  // (before, before_id, since, limit) → (items, next_since, next_before_id)
  // shape; see CursorPageRequest in api/types.ts. `lang` threads through
  // for the two language-aware endpoints (briefings + analysis history).
  listCategoryBriefingsPage: (
    categoryId: string,
    params: CursorPageRequest & { lang?: string | null } = {},
  ) => {
    const { lang, ...cursor } = params;
    return apiFetch<BriefingHistoryResponse>(
      withLang(
        buildCursorPath(
          `/api/v1/categories/${encodeURIComponent(categoryId)}/briefings`,
          cursor,
        ),
        lang,
      ),
    );
  },
  listMaterialClustersPage: (materialId: string, params: CursorPageRequest = {}) =>
    apiFetch<MaterialClustersPageResponse>(
      buildCursorPath(
        `/api/v1/materials/${encodeURIComponent(materialId)}/clusters`,
        params,
      ),
    ),
  listMaterialAnalysisHistoryPage: (
    materialId: string,
    params: CursorPageRequest & { include_body?: boolean; lang?: string | null } = {},
  ) => {
    const { include_body, lang, ...cursor } = params;
    let path = buildCursorPath(
      `/api/v1/materials/${encodeURIComponent(materialId)}/analysis/history`,
      cursor,
    );
    if (include_body) {
      path += path.includes("?") ? "&include_body=true" : "?include_body=true";
    }
    return apiFetch<AnalysisHistoryResponse>(withLang(path, lang));
  },
};

function buildCursorPath(base: string, params: CursorPageRequest): string {
  const qs = new URLSearchParams();
  if (params.since) qs.set("since", params.since);
  if (params.before) qs.set("before", params.before);
  if (params.before_id != null) qs.set("before_id", String(params.before_id));
  if (params.limit != null) qs.set("limit", String(params.limit));
  const q = qs.toString();
  return q ? `${base}?${q}` : base;
}

export const __testing = {
  clearEtagCache: () => etagCache.clear(),
};

export { ApiError };
